import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useCallback, useContext } from "react";
import { AudioPlayerContext } from "../../components/audioPlayer/context.js";
import { findFragmentIndexForKeyMoment } from "./gradingHelpers.js";
import type {
  CallPosition,
  TranscriptPosition,
} from "./transcriptNavigationHelpers.js";

interface UseTranscriptNavigationActionsOptions {
  recordings: readonly AccountGradingRecording[];
  onPositionRequest: (position: TranscriptPosition) => void;
}

/** Selects transcript positions, moving call audio without changing playback state; texts leave audio alone. */
export function useTranscriptNavigationActions(
  options: UseTranscriptNavigationActionsOptions,
) {
  const { recordings, onPositionRequest } = options;
  const audioPlayer = useContext(AudioPlayerContext);
  const selectPosition = useCallback(
    (position: CallPosition) => {
      if (audioPlayer.nativeAudio && !audioPlayer.nativeAudio.paused) {
        audioPlayer.play(position.recordingId, position.timestamp / 1000);
      } else {
        audioPlayer.seek(position.recordingId, position.timestamp / 1000);
      }
      onPositionRequest(position);
    },
    [audioPlayer, onPositionRequest],
  );

  const selectCall = useCallback(
    (recordingId: RecordingId) => {
      const firstFragment = recordings.find(
        (recording) => recording.id === recordingId,
      )?.transcript?.fragments[0];
      selectPosition(
        firstFragment
          ? {
              type: "call",
              recordingId,
              timestamp: firstFragment.start_time,
              fragmentIndex: 0,
            }
          : { type: "call", recordingId, timestamp: 0 },
      );
    },
    [recordings, selectPosition],
  );

  const selectSegment = useCallback(
    (position: CallPosition) => {
      const fragments = recordings.find(
        (recording) => recording.id === position.recordingId,
      )?.transcript?.fragments;
      if (!fragments) {
        selectPosition(position);
        return;
      }
      const fragmentIndex = findFragmentIndexForKeyMoment(
        fragments,
        position.timestamp,
      );
      const fragment =
        fragmentIndex === undefined ? undefined : fragments[fragmentIndex];
      selectPosition(
        fragment
          ? { ...position, timestamp: fragment.start_time, fragmentIndex }
          : position,
      );
    },
    [recordings, selectPosition],
  );

  // A conversation opens at its first text, as a call opens at its first fragment
  const selectTextConversation = useCallback(
    (textConversationId: CallTaskEventId) =>
      onPositionRequest({
        type: "textMessage",
        textMessageId: textConversationId,
      }),
    [onPositionRequest],
  );

  return { selectCall, selectSegment, selectTextConversation };
}
